import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import NavBar from '../../components/common/NavBar';
import Icon from '../../components/common/Icon';
import Button from '../../components/elements/Button';
import { apiRequest, teamPhoto } from '../../utils';
import TeamPaymentBanner, { useTeamPaymentLock } from '../../components/team/TeamPaymentLock';

// Self-serve team merging: flag your team "open to merge", browse the other open
// teams on your event, send a merge request; the other captain accepts (your
// players and tickets move to their team) or declines. Day-of merges for the
// back office live in Machina — this page is the player-facing flow.
const Page = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [candidates, setCandidates] = useState(null);
  const [requests, setRequests] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmReq, setConfirmReq] = useState(null);

  const reload = () => {
    apiRequest('GET', '/merge-candidates').then(setCandidates).catch(() => setCandidates({ candidates: [] }));
    apiRequest('GET', '/merge-requests').then(setRequests).catch(() => setRequests(null));
  };
  useEffect(reload, []);

  const act = async (method, url, body, doneKey) => {
    setBusy(true);
    try {
      await apiRequest(method, url, body);
      if (doneKey) setToast(t(doneKey));
      reload();
    } catch (e) {
      setToast(t(e.message || 'somethingWentWrong'));
    } finally {
      setBusy(false);
    }
  };

  const isCaptain = requests?.is_captain;
  const open = !!(requests?.open ?? candidates?.open);
  // Merging is only for friends teams — a company/community team gets a notice
  // instead of the toggle + candidate list. Default true while still loading.
  const canMerge = candidates?.can_merge !== false;
  // An unpaid team (online payment failed) merges with nobody until it is paid.
  const payLock = useTeamPaymentLock({ ownTeam: true });

  return (
    <Screen color='white' p='3'>
      <div className='top'>
        <Title>{t('mergeTeams')}</Title>
        <p className='text-secondary' style={{ fontSize: '0.9rem' }}>{t('mergeTeamsIntro')}</p>
        <TeamPaymentBanner lock={payLock} />

        {toast && (
          <div className='alert alert-info py-2' role='alert' onClick={() => setToast(null)}>
            {toast}
          </div>
        )}

        {!canMerge && (
          <div className='alert alert-secondary py-2' role='alert'>{t('mergeFriendsOnly')}</div>
        )}

        {/* Open-to-merge toggle (captain only) — friends teams only */}
        {canMerge && !payLock && (
          <div className='form-check form-switch mb-3'>
            <input
              className='form-check-input'
              type='checkbox'
              id='openToMerge'
              checked={open}
              disabled={busy || !isCaptain}
              onChange={(e) => act('POST', '/team-open-merge', { open: e.target.checked }, e.target.checked ? 'openToMergeOn' : 'openToMergeOff')}
            />
            <label className='form-check-label' htmlFor='openToMerge'>{t('openToMerge')}</label>
            {!isCaptain && <div className='text-secondary' style={{ fontSize: '0.85rem' }}>{t('captainOnly')}</div>}
          </div>
        )}

        {/* Incoming requests — the target captain decides */}
        {requests?.incoming?.length > 0 && (
          <>
            <h5>{t('incomingMergeRequests')}</h5>
            {requests.incoming.map((r) => (
              <div key={r.id} className='card mb-2'>
                <div className='card-body d-flex align-items-center justify-content-between py-2'>
                  <div role='button' tabIndex={0} style={{ cursor: 'pointer' }}
                       onClick={() => navigate(`/public-team/${r.team_id}`)}
                       onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/public-team/${r.team_id}`); }}>
                    <strong className='text-decoration-underline'>{r.name}</strong>
                    <div className='text-secondary' style={{ fontSize: '0.85rem' }}>
                      {t('playersCount', { count: r.players })}
                      <span className='ms-1 text-primary'>· {t('viewTeam')}</span>
                    </div>
                  </div>
                  {isCaptain && (
                    <div className='d-flex gap-2'>
                      <button className='btn btn-sm btn-success' disabled={busy}
                              onClick={() => setConfirmReq(r)}>
                        {t('accept')}
                      </button>
                      <button className='btn btn-sm btn-outline-secondary' disabled={busy}
                              onClick={() => act('POST', '/merge-decline', { request_id: r.id }, 'mergeDeclined')}>
                        {t('decline')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Outgoing requests — cancellable while pending */}
        {requests?.outgoing?.length > 0 && (
          <>
            <h5>{t('outgoingMergeRequests')}</h5>
            {requests.outgoing.map((r) => (
              <div key={r.id} className='card mb-2'>
                <div className='card-body d-flex align-items-center justify-content-between py-2'>
                  <div><strong>{r.name}</strong></div>
                  {isCaptain && (
                    <button className='btn btn-sm btn-outline-secondary' disabled={busy}
                            onClick={() => act('POST', '/merge-cancel', { request_id: r.id }, 'mergeCancelled')}>
                      {t('cancel')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Candidates — the other open teams on this event (friends teams only) */}
        {canMerge && !payLock && (
          <>
            <h5 className='mt-3'>{t('teamsOpenToMerge')}</h5>
            {!candidates && <p className='text-secondary'>{t('loading')}</p>}
            {candidates && candidates.candidates?.length === 0 && (
              <p className='text-secondary'>{t('noTeamsOpenToMerge')}</p>
            )}
            {candidates?.candidates?.map((c) => (
              <div key={c.id} className='card mb-2'>
                <div className='card-body d-flex align-items-center justify-content-between py-2'>
                  <div className='d-flex align-items-center gap-2' role='button' tabIndex={0} style={{ cursor: 'pointer' }}
                       onClick={() => navigate(`/public-team/${c.id}`)}
                       onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/public-team/${c.id}`); }}>
                    <img src={teamPhoto(c.picture)} alt='' style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8 }} />
                    <div>
                      <strong className='text-decoration-underline'>{c.name}</strong>
                      <div className='text-secondary' style={{ fontSize: '0.85rem' }}>
                        {t('playersCount', { count: c.players })}
                        {!c.fits && <span className='ms-1 text-danger'>{t('combinedTooBig')}</span>}
                      </div>
                    </div>
                  </div>
                  {isCaptain && (
                    <button className='btn btn-sm btn-primary' disabled={busy || !c.fits}
                            onClick={() => window.confirm(t('confirmMergeRequest', { name: c.name })) && act('POST', '/merge-request', { target_team_id: c.id }, 'mergeRequestSent')}>
                      <Icon name='users' className='me-1' />{t('requestMerge')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        <p className='text-secondary mt-3' style={{ fontSize: '0.85rem' }}>{t('mergeTeamsFootnote')}</p>

        <Button className='mt-2' onClick={() => navigate('/')}>{t('backToHome')}</Button>

        {/* Accept confirmation — spell out exactly what a merge does, since it's
            permanent (players + tickets move, the other team is closed). */}
        {confirmReq && (
          <div onClick={() => setConfirmReq(null)}
               style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '1rem' }}>
            <div onClick={(e) => e.stopPropagation()}
                 style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', maxWidth: 420, width: '100%', boxShadow: '0 10px 40px -10px rgba(0,0,0,0.4)' }}>
              <h5 className='mb-2'>{t('confirmMergeTitle')}</h5>
              <p className='mb-2'>{t('confirmMergeIntro', { name: confirmReq.name, count: confirmReq.players })}</p>
              <ul className='text-secondary mb-0' style={{ fontSize: '0.9rem', paddingLeft: '1.1rem' }}>
                <li>{t('confirmMergePlayers', { count: confirmReq.players })}</li>
                <li>{t('confirmMergeTickets')}</li>
                <li>{t('confirmMergeClose', { name: confirmReq.name })}</li>
                <li>{t('confirmMergePermanent')}</li>
              </ul>
              <div className='d-flex gap-2 justify-content-end mt-3'>
                <button className='btn btn-outline-secondary' disabled={busy}
                        onClick={() => setConfirmReq(null)}>{t('cancel')}</button>
                <button className='btn btn-success' disabled={busy}
                        onClick={() => { const r = confirmReq; setConfirmReq(null); act('POST', '/merge-accept', { request_id: r.id }, 'mergeDone'); }}>
                  {t('confirmAcceptMerge')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className='bottom'>
        <NavBar />
      </div>
    </Screen>
  );
};

export default Page;
